import { Dashboard } from '@protos/v2/dashboard';
import { logger } from '@services/context';
import { toastifyService } from '@services/ToastifyService';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useDashboardList = (
  updateDashboard: (dashboard: Dashboard) => void,
  deleteDashboard: (dashboardId: string) => void,
  addDashboard: () => Promise<Dashboard>,
  setCurrentDashboardById: (dashboardId: string) => void,
  navigate: (path: string) => void,
  allDashboards: Dashboard[],
  selectedDashboard?: Dashboard
) => {
  const [isEditingDashboard, setIsEditingDashboard] = useState<{ isEditing: boolean; id: number | null }>({ isEditing: false, id: null });
  const [editingDashboardName, setEditingDashboardName] = useState<string | undefined>();
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isInvalidEditingDashboardName = useMemo(
    () => !editingDashboardName || allDashboards.some(d => d.id !== isEditingDashboard.id && d.name === editingDashboardName),
    [allDashboards, editingDashboardName, isEditingDashboard]
  );

  const onConfirmEditingChanges = useCallback(async () => {
    if (!isEditingDashboard.id || !editingDashboardName) return;

    const dashboardBeingEdited = allDashboards.find(d => d.id === isEditingDashboard.id);
    if (!dashboardBeingEdited) return;

    const transformedDashboard = {
      ...dashboardBeingEdited,
      name: editingDashboardName,
    };
    updateDashboard(transformedDashboard);

    setIsEditingDashboard({ isEditing: false, id: null });
    setEditingDashboardName(undefined);
  }, [isEditingDashboard, editingDashboardName, updateDashboard, allDashboards]);

  const onConfirmDeleteDashboard = useCallback(
    (dashboardId: number) => {
      deleteDashboard(dashboardId.toString());
    },
    [deleteDashboard]
  );

  const onCreateNewDashboard = useCallback(async () => {
    try {
      const dashboard = await addDashboard();

      navigate(`/dashboard/${dashboard.id}`);
    } catch (error: any) {
      toastifyService.showErrorMessage('Unable to create dashboard, please try again');
      logger.error('Error creating dashboard', error);
    }
  }, [navigate, addDashboard]);

  const onDashboardClick = useCallback(
    (id: number) => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      } else {
        clickTimerRef.current = setTimeout(() => {
          if (isEditingDashboard.isEditing) {
            setIsEditingDashboard({ isEditing: false, id: null });
            setEditingDashboardName(undefined);
          }

          if (selectedDashboard?.id !== id) {
            setCurrentDashboardById(id.toString());
            navigate(`/dashboard/${id}`);
          }
          clickTimerRef.current = null;
        }, 200);
      }
    },
    [navigate, isEditingDashboard, setCurrentDashboardById, selectedDashboard]
  );

  const onDashboardDoubleClick = useCallback(
    (id: number, event: React.MouseEvent<HTMLDivElement>) => {
      event?.stopPropagation();
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }

      setIsEditingDashboard({ isEditing: true, id: id });

      const dashboardName = allDashboards.find(d => d.id === id)?.name;
      if (dashboardName) {
        setEditingDashboardName(dashboardName);
      }
    },
    [allDashboards]
  );

  const onEditDashboardName = useCallback(
    (id: number, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event?.stopPropagation();
      setIsEditingDashboard({ isEditing: true, id });
      const dashboardName = allDashboards.find(d => d.id === id)?.name;
      if (dashboardName) {
        setEditingDashboardName(dashboardName);
      }
    },
    [allDashboards]
  );

  useEffect(() => {
    if (selectedDashboard) {
      setEditingDashboardName(selectedDashboard.name);
    }
  }, [selectedDashboard]);

  return {
    onCreateNewDashboard,
    onDashboardClick,
    onDashboardDoubleClick,
    onEditDashboardName,
    onConfirmEditingChanges,
    onConfirmDeleteDashboard,
    isInvalidEditingDashboardName,
    isEditingDashboard,
    editingDashboardName,
    setEditingDashboardName,
    setIsEditingDashboard,
  };
};
